import {
    AbcOutlined,
    CheckCircleRounded,
    CloseOutlined,
    DoNotDisturbOnRounded,
    DriveFileRenameOutlineOutlined,
    KeyOutlined,
    PlaceOutlined,
    RemoveCircleOutlineOutlined,
    SaveOutlined,
    WhatsApp,
} from "@mui/icons-material";

import {
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Paper,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";

import { GridColDef } from "@mui/x-data-grid";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { SetterOrUpdater, useRecoilValue } from "recoil";
import { ContentProps, Toast } from "primereact/toast";
import { SketchPicker } from 'react-color';

import L from "leaflet";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { userAtom } from "../../../atoms/atoms";
import GridTemplate from "../../../components/GridTemplate";
import Rmodal from "../../../components/Rmodal";
import { getFilteredList, setToastRefToOpen, buildInterestPointShareMessage, buildAllInterestPointsShareMessage, openWhatsAppShare } from "../../../functions/functions";
import activateInterestPointCrudRequest from "../../../resources/Api/interestPoint/activateInterestPointCrudRequest";
import createInterestPointCrudRequest from "../../../resources/Api/interestPoint/createInterestPointCrudRequest";
import findAllInterestPointsByCustomerIdCrudResponse from "../../../resources/Api/interestPoint/findAllInterestPointsByCustomerIdCrudResponse";
import inactivateInterestPointCrudRequest from "../../../resources/Api/interestPoint/inactivateInterestPointCrudRequest";
import updateInterestPointCrudRequest from "../../../resources/Api/interestPoint/updateInterestPointCrudRequest";
import {
    InterestPointRequestType,
    InterestPointType,
    SelectedParametersType,
    UserType,
} from "../../../types/types";

const columns: GridColDef[] = [
    {
        field: "activated",
        type: "boolean",
        width: 150,
        editable: false,
        sortable: false,
        renderHeader: () => (
            <Chip sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }} label={"Ativo"} icon={<KeyOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />} />
        ),
        renderCell: (item) => (
            <Chip sx={{ background: "transparent" }} icon={item.row.activated ? <CheckCircleRounded className="activated" style={{ height: 28, width: 28 }} /> : <DoNotDisturbOnRounded className="deactivated" style={{ height: 28, width: 28 }} />} />
        ),
    },
    {
        field: "name",
        align: "left",
        width: 220,
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }} label={"Nome"} icon={<PlaceOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />} />
        ),
        renderCell: (item) => <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={item.row.name} />,
    },
    {
        field: "description",
        headerClassName: "last-column",
        cellClassName: "last-column",
        align: "left",
        editable: false,
        sortable: true,
        renderHeader: () => (
            <Chip sx={{ color: "#fff", background: "transparent", fontFamily: "Prometo" }} label={"Descrição"} icon={<AbcOutlined style={{ height: 30, width: 30, fill: "#ffff" }} />} />
        ),
        renderCell: (item) => <Chip sx={{ background: "transparent", fontFamily: "Prometo" }} label={item.row.description} />,
    },
];

const rowsOptions = (
    list: Array<InterestPointType>,
    openDetails?: SetterOrUpdater<boolean>
): Array<InterestPointType> => {
    const result = [...list];
    return result.map((item) => ({ ...item, openDetails }));
};

/**
 * LocationPicker - captura o clique no mapa e devolve lat/lng para o componente pai.
 * Precisa ser um componente próprio pois useMapEvents só pode ser chamado
 * dentro de um filho do MapContainer.
 */
const LocationPicker = ({
    disabled,
    onPick,
}: {
    disabled: boolean;
    onPick: (lat: number, lng: number) => void;
}) => {
    useMapEvents({
        click(e) {
            if (disabled) return;
            onPick(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
};

/**
 * RecenterMap - centraliza o mapa sempre que a posição (lat/lng) mudar,
 * necessário porque o MapContainer só usa a prop `center` na primeira renderização.
 */
const RecenterMap = ({ lat, lng }: { lat: number; lng: number }) => {
    const map = useMap();
    useEffect(() => {
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            map.flyTo([lat, lng], map.getZoom() < 13 ? 15 : map.getZoom());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lat, lng]);
    return null;
};

/**
 * InterestPointPage - Screen that renders a page to create and manage interest points
 * @component-screen
 */
const InterestPointPage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [loading, setLoading] = useState<boolean>(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState<boolean>(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");
    const [filteredList, setFilteredList] = useState<Array<InterestPointType>>([]);

    const [isLastPage, setIsLastPage] = useState<boolean>(true);
    const [numberOfElementsPerPage, setNumberOfElementsPerPage] = useState<number>(0);
    const [numberOfElements, setNumberOfElements] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);

    // states
    const [interestPointsList, setInterestPointList] = useState<Array<InterestPointType>>([]);
    const [selectedInterestPoint, setSelectedInterestPoint] = useState<InterestPointType>({
        id: -1,
        name: "",
        description: "",
        latitude: 0,
        longitude: 0,
        address: "",
        color: "",
        customerId: -1,
        activated: true,
    });

    const [interestPointName, setInterestPointName] = useState<string>("");
    const [interestPointDescription, setInterestPointDescription] = useState<string>("");
    const [interestPointLatitude, setInterestPointLatitude] = useState<string>("");
    const [interestPointLongitude, setInterestPointLongitude] = useState<string>("");
    const [interestPointAddress, setInterestPointAddress] = useState<string>("");
    const [interestPointColor, setInterestPointColor] = useState<string>("");

    // refs
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);
    // controla a direção da última atualização, evita loop entre geocodificação de endereço e reversa do mapa
    const geocodeSourceRef = useRef<"address" | "map" | null>(null);

    // cookies
    const [cookies] = useCookies(["selectedParameters", "token"]);

    // hooks and consts
    const location = useLocation();
    const currentParameters: SelectedParametersType = useMemo(() => cookies.selectedParameters, [cookies.selectedParameters]);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(ref, validationText, "Atenção!", statusMessage, undefined, 3000, false);
        }
    }, [isOpenDetails, validationText, isFieldsValid, statusMessage, isToastVisible]);

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setInEdit(false);
        setInInsert(false);
        setIsOpenDetails(false);
        setInterestPointName("");
        setInterestPointDescription("");
        setInterestPointAddress("");
        setInterestPointColor("");
        setInterestPointLatitude("");
        setInterestPointLongitude("");
        setSelectedInterestPoint({
            id: -1,
            name: "",
            description: "",
            latitude: 0,
            longitude: 0,
            address: "",
            color: "",
            customerId: -1,
            activated: true,
        });
    }, []);

    // busca principal — agora atualiza corretamente os campos de paginação e loading
    const getAllInterestPoints = useCallback(async (status: string = "activated", page: number = 0, size: number = 20) => {
        const token = cookies.token || sessionStorage.getItem("token");
        setLoading(true);
        try {
            const { data } = await findAllInterestPointsByCustomerIdCrudResponse(
                token,
                getCurrentUser.id,
                currentParameters.customer.id,
                status,
                page,
                size,
            );
            setInterestPointList(data.content ?? []);
            setIsLastPage(data.last);
            setNumberOfElementsPerPage(data.numberOfElements);
            setPageNumber(data.pageable.pageNumber);

            if (data.first) {
                setNumberOfElements(data.totalElements);
            }
        } catch (error) {
            console.log(error);
            setInterestPointList([]);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 2000);
        }
    }, [cookies.token, currentParameters.customer.id, getCurrentUser.id]);

    useEffect(() => {
        getAllInterestPoints();
    }, [getAllInterestPoints]);

    // fields corretos: (campos, lista, query) — igual ao GeofencePage
    useEffect(() => {
        setFilteredList(getFilteredList(["name", "description"], interestPointsList, query));
    }, [interestPointsList, query]);

    const onSave = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");

        setLoading(true);
        const payload: InterestPointRequestType = {
            id: -1,
            name: interestPointName,
            description: interestPointDescription,
            latitude: Number(interestPointLatitude),
            longitude: Number(interestPointLongitude),
            address: interestPointAddress,
            color: interestPointColor,
            customerId: currentParameters.customer.id,
        };

        if (!payload.name || !payload.color || Number.isNaN(payload.latitude) || Number.isNaN(payload.longitude) || interestPointLatitude === "" || interestPointLongitude === "") {
            setTextValidation("Nome, cor e localização no mapa são obrigatórios, preencha e tente novamente.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await createInterestPointCrudRequest(token, payload);
            setTextValidation("Registro salvo com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllInterestPoints();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;
            const errors = (data?.reason ?? data?.message ?? "Não foi possível salvar o ponto de interesse.").split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        currentParameters.customer.id,
        cookies.token,
        interestPointName,
        interestPointDescription,
        interestPointLatitude,
        interestPointLongitude,
        interestPointAddress,
        interestPointColor,
        handleClose,
        getAllInterestPoints,
    ]);

    const onUpdate = useCallback(async () => {
        const token = cookies.token || sessionStorage.getItem("token");

        setLoading(true);
        const payload: InterestPointRequestType = {
            id: selectedInterestPoint.id,
            name: interestPointName,
            description: interestPointDescription,
            latitude: Number(interestPointLatitude),
            longitude: Number(interestPointLongitude),
            address: interestPointAddress,
            color: interestPointColor,
            customerId: selectedInterestPoint.customerId,
        };

        if (!payload.name || !payload.color || Number.isNaN(payload.latitude) || Number.isNaN(payload.longitude) || interestPointLatitude === "" || interestPointLongitude === "") {
            setTextValidation("Nome, cor e localização no mapa são obrigatórios, preencha e tente novamente.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        if (
            payload.name === selectedInterestPoint.name &&
            payload.description === selectedInterestPoint.description &&
            payload.address === selectedInterestPoint.address &&
            payload.color === selectedInterestPoint.color &&
            payload.latitude === selectedInterestPoint.latitude &&
            payload.longitude === selectedInterestPoint.longitude
        ) {
            setTextValidation("Nada foi alterado.");
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setLoading(false);
            return;
        }

        try {
            await updateInterestPointCrudRequest(token, payload.id, payload);
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            setTimeout(() => {
                handleClose();
                getAllInterestPoints();
            }, 3000);
        } catch (error: any) {
            setLoading(false);
            const { data } = error.response;
            const errors = (data?.reason ?? data?.message ?? "Não foi possível atualizar o ponto de interesse.").split(".");
            errors.forEach((message: any, index: number) => {
                if (index === 0) {
                    setFieldsIsValid(false);
                    setStatusMessage("warn");
                    setTextValidation(message);
                }
            });
        }
    }, [
        selectedInterestPoint,
        cookies.token,
        interestPointName,
        interestPointDescription,
        interestPointLatitude,
        interestPointLongitude,
        interestPointAddress,
        interestPointColor,
        handleClose,
        getAllInterestPoints,
    ]);

    const onUpdateStatus = useCallback(async (item: InterestPointType) => {
        const token = cookies.token || sessionStorage.getItem("token");
        setLoading(true);
        try {
            if (item.activated) {
                await inactivateInterestPointCrudRequest(token, currentParameters.customer.id, item.id);
            } else {
                await activateInterestPointCrudRequest(token, currentParameters.customer.id, item.id);
            }
            setTextValidation("Registro atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);
        } catch (error: any) {
            const { data } = error.response;
            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data?.reason ?? data?.message ?? "Não foi possível atualizar o registro.");
        } finally {
            setTimeout(() => {
                handleClose();
                getAllInterestPoints();
            }, 3000);
        }
    }, [cookies.token, currentParameters.customer.id, getAllInterestPoints, handleClose]);

    // confirmação via toast, igual ao GeofencePage — troca o window.confirm
    const verifyBeforeUpdateStatus = useCallback((item: InterestPointType) => {
        const ref = toastModalRef;
        if (!isToastVisible) {
            setToastRefToOpen(
                ref,
                "Deseja realmente atualizar o status desse registro?",
                "Atenção!",
                statusMessage,
                {
                    content: (props: ContentProps) => (
                        <Box className="flex flex-column align-items-left" style={{ flex: "1" }}>
                            <Typography>{props?.message.summary}</Typography>
                            <Box className="font-medium text-lg my-3 text-900">{props?.message.detail}</Box>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                    onUpdateStatus(item);
                                }}
                            >
                                Sim
                            </Button>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                }}
                            >
                                Não
                            </Button>
                        </Box>
                    ),
                },
                undefined,
                true
            );
            setIsToastVisible(true);
        }
    }, [onUpdateStatus, isToastVisible, statusMessage]);

    const verifyBeforeCancel = useCallback(() => {
        const ref = toastModalRef;
        let canCancel = true;

        if (inEdit && !inInsert) {
            if (
                interestPointName !== selectedInterestPoint.name ||
                interestPointDescription !== (selectedInterestPoint.description ?? "") ||
                interestPointAddress !== (selectedInterestPoint.address ?? "") ||
                interestPointColor !== (selectedInterestPoint.color ?? "") ||
                interestPointLatitude !== selectedInterestPoint.latitude.toString() ||
                interestPointLongitude !== selectedInterestPoint.longitude.toString()
            ) {
                canCancel = false;
            }
        } else if (!inEdit && inInsert && (interestPointName || interestPointDescription || interestPointAddress || interestPointLatitude || interestPointLongitude)) {
            canCancel = false;
        }

        if (!canCancel) {
            if (!isToastVisible) {
                setToastRefToOpen(
                    ref,
                    "Existem dados não salvos, deseja realmente cancelar?",
                    "Atenção!",
                    statusMessage,
                    {
                        content: (props: ContentProps) => (
                            <Box className="flex flex-column align-items-left" style={{ flex: "1" }}>
                                <Typography>{props?.message.summary}</Typography>
                                <Box className="font-medium text-lg my-3 text-900">{props?.message.detail}</Box>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        handleClose();
                                    }}
                                >
                                    Sim
                                </Button>
                                <Button
                                    className="p-button-sm flex"
                                    style={{ cursor: "pointer", color: "inherit" }}
                                    onClick={() => {
                                        ref.current?.clear();
                                        setIsToastVisible(false);
                                    }}
                                >
                                    Não
                                </Button>
                            </Box>
                        ),
                    },
                    undefined,
                    true
                );
                setIsToastVisible(true);
            }
        } else {
            handleClose();
        }
    }, [
        inEdit,
        inInsert,
        handleClose,
        statusMessage,
        isToastVisible,
        interestPointName,
        interestPointDescription,
        interestPointAddress,
        interestPointColor,
        interestPointLatitude,
        interestPointLongitude,
        selectedInterestPoint,
    ]);

    const handleOnEditValues = useCallback((item: InterestPointType) => {
        setInterestPointName(item.name);
        setInterestPointDescription(item.description ?? "");
        setInterestPointLatitude(item.latitude.toString());
        setInterestPointLongitude(item.longitude.toString());
        setInterestPointAddress(item.address ?? "");
        setInterestPointColor(item.color ?? "");
    }, []);

    const handleChange = useCallback((name: string, value: string) => {
        if (name === "name") setInterestPointName(value);
        if (name === "description") setInterestPointDescription(value);
        if (name === "address") setInterestPointAddress(value);
        if (name === "latitude") setInterestPointLatitude(value);
        if (name === "longitude") setInterestPointLongitude(value);
    }, []);

    // só edita quando é um registro novo (inInsert) ou quando o usuário clicou em "Editar" (inEdit)
    const isReadOnly = useMemo(
        () => selectedInterestPoint?.id !== -1 && !inEdit && !inInsert,
        [selectedInterestPoint?.id, inEdit, inInsert]
    );

    // geocodificação reversa: mapa -> endereço (clique ou arraste do marcador)
    const reverseGeocode = useCallback(async (lat: number, lng: number) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
                { headers: { "Accept-Language": "pt-BR" } }
            );
            const data = await response.json();
            if (data?.display_name) {
                // marca a origem como "map" pra não disparar a geocodificação de endereço em seguida
                geocodeSourceRef.current = "map";
                setInterestPointAddress(data.display_name);
            }
        } catch (error) {
            console.log(error);
        }
    }, []);

    // geocodificação direta: endereço -> mapa (digitação com debounce)
    const geocodeAddress = useCallback(async (address: string) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`,
                { headers: { "Accept-Language": "pt-BR" } }
            );
            const data = await response.json();
            if (data && data.length > 0) {
                geocodeSourceRef.current = "address";
                setInterestPointLatitude(Number(data[0].lat).toFixed(6));
                setInterestPointLongitude(Number(data[0].lon).toFixed(6));
            }
        } catch (error) {
            console.log(error);
        }
    }, []);

    // dispara a geocodificação do endereço digitado, com debounce, exceto quando o endereço
    // acabou de vir do mapa (evita loop endereço -> mapa -> endereço)
    useEffect(() => {
        if (isReadOnly) return;
        if (geocodeSourceRef.current === "map") {
            geocodeSourceRef.current = null;
            return;
        }
        if (!interestPointAddress || interestPointAddress.trim().length < 5) return;

        const timeoutId = setTimeout(() => {
            geocodeAddress(interestPointAddress);
        }, 900);

        return () => clearTimeout(timeoutId);
    }, [interestPointAddress, isReadOnly, geocodeAddress]);

    // centro do mapa: usa o ponto já selecionado, senão cai no centro padrão (mesmo usado no GeofencePage)
    const getMapCenter = useMemo(() => {
        const lat = Number(interestPointLatitude);
        const lng = Number(interestPointLongitude);
        if (interestPointLatitude && interestPointLongitude && !Number.isNaN(lat) && !Number.isNaN(lng)) {
            return { lat, lng };
        }
        return { lat: -15.584697, lng: -56.087066 };
    }, [interestPointLatitude, interestPointLongitude]);

    // marcador em formato de "pin", evita depender das imagens padrão do Leaflet (que costumam quebrar com bundlers)
    const pinIcon = useMemo(
        () =>
            L.divIcon({
                className: "interest-point-marker",
                html: `<div style="background:${interestPointColor || "#003f4a"};width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
                iconSize: [22, 22],
                iconAnchor: [11, 22],
            }),
        [interestPointColor]
    );

    const hasValidPosition = useMemo(() => {
        const lat = Number(interestPointLatitude);
        const lng = Number(interestPointLongitude);
        return interestPointLatitude !== "" && interestPointLongitude !== "" && !Number.isNaN(lat) && !Number.isNaN(lng);
    }, [interestPointLatitude, interestPointLongitude]);

    const loadingEsqueleton = useMemo(() => {
        return (
            <Grid className="container" sx={{ height: 500, width: 540 }}>
                {Array.from(new Array(1)).map((_, i) => (
                    <Box key={i} sx={{ width: "100%", height: 500, padding: 5 }}>
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                        <Skeleton height={56} />
                    </Box>
                ))}
            </Grid>
        );
    }, []);

    const DetailsModal = useMemo(() => {
        return (
            <Rmodal open={isOpenDetails} hiddenBackdrop={false} style={{ zIndex: 99999 }}
                toast={
                    <Toast
                        ref={toastModalRef}
                        position="center"
                        onShow={() => {
                            setIsToastVisible(true);
                            if (validationText) {
                                setTimeout(() => {
                                    setFieldsIsValid(true);
                                    setIsToastVisible(false);
                                }, 3000);
                            }
                        }}
                    />
                }
            >
                <Paper
                    sx={{
                        ...modalStyle,
                        width: { xs: 320, sm: 480, md: 700 },
                        maxHeight: "90vh",
                        overflow: "auto",
                    }}
                >
                    {!loading ? (
                        <>
                            <Box className="row" style={{ padding: 0, marginBottom: 10 }}>
                                <Box className="col-sm-12 col-md-7 d-none d-md-flex" style={{ marginBottom: 10 }}>
                                    <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                        Identificação
                                    </Typography>
                                </Box>
                                <Box className="col-sm-12 col-md-5" style={{ display: "flex", justifyContent: "end" }}>
                                    {selectedInterestPoint?.id !== -1 && (
                                        <>
                                            <Chip
                                                style={{
                                                    color: selectedInterestPoint.activated ? "#00B700" : "#dc3545",
                                                    marginRight: 5,
                                                    fontWeight: "bolder",
                                                }}
                                                clickable={false}
                                                onClick={() => {}}
                                                label={selectedInterestPoint.activated ? "Ativo" : "Inativo"}
                                            />
                                            {selectedInterestPoint.activated && (
                                                <Chip
                                                    style={{ color: "black", marginRight: 5 }}
                                                    disabled={!location?.state?.canEdit || (process.env.NODE_ENV == 'production' && localStorage.getItem("impersonating") === "true")}
                                                    onClick={() => setInEdit(true)}
                                                    label="Editar"
                                                    icon={<DriveFileRenameOutlineOutlined style={{ height: 24, width: 24, fill: "black" }} />}
                                                />
                                            )}
                                            <Chip
                                                style={{ color: "black", marginRight: 5 }}
                                                disabled={!location?.state?.canEdit || (process.env.NODE_ENV == 'production' && localStorage.getItem("impersonating") === "true")}
                                                onClick={() => {
                                                    if (!isToastVisible) {
                                                        verifyBeforeUpdateStatus(selectedInterestPoint);
                                                    }
                                                }}
                                                label={selectedInterestPoint.activated ? "Desativar" : "Ativar"}
                                                icon={<RemoveCircleOutlineOutlined style={{ height: 24, width: 24, fill: "black" }} />}
                                            />
                                            <Chip
                                                style={{ color: "#25D366", marginRight: 5 }}
                                                onClick={() => openWhatsAppShare(buildInterestPointShareMessage(selectedInterestPoint))}
                                                label="Compartilhar"
                                                icon={<WhatsApp style={{ height: 24, width: 24, fill: "#25D366" }} />}
                                            />
                                        </>
                                    )}
                                </Box>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl className="form-group" style={{ display: "flex", width: "100%" }}>
                                        <TextField
                                            label='Nome'
                                            name="name"
                                            autoComplete="off"
                                            required
                                            disabled={isReadOnly}
                                            value={interestPointName}
                                            onChange={(e) => handleChange(e.target.name, e.target.value)}
                                            placeholder="Digite o nome"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl className="form-group" style={{ display: "flex", width: "100%" }}>
                                        <TextField
                                            label="Descrição"
                                            name="description"
                                            autoComplete="off"
                                            disabled={isReadOnly}
                                            value={interestPointDescription}
                                            onChange={(e) => handleChange(e.target.name, e.target.value)}
                                            placeholder="Digite a descrição"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        name="address"
                                        label="Endereço"
                                        placeholder="Digite o endereço para localizar no mapa"
                                        disabled={isReadOnly}
                                        value={interestPointAddress}
                                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontFamily: "Prometo", mb: 1 }}>
                                        Cor *
                                    </Typography>
                                    <SketchPicker
                                        color={interestPointColor || "rgb(0, 63, 74)"}
                                        disableAlpha
                                        onChange={(color) => {
                                            if (!isReadOnly) setInterestPointColor(color.hex);
                                        }}
                                    />
                                </Grid>
                            </Grid>

                            <Box className="col-sm-12 col-md-7 d-none d-md-flex" style={{ marginTop: 15 }}>
                                <Typography variant="body1" sx={{ fontWeight: "bolder" }}>
                                    Mapa
                                </Typography>
                            </Box>
                            {!isReadOnly && (
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    Digite um endereço para localizar no mapa, ou clique/arraste o marcador para preencher o endereço automaticamente.
                                </Typography>
                            )}

                            <Box style={{ margin: "10px 0px 15px 0px" }}>
                                <Box
                                    sx={{
                                        padding: 0,
                                        "& .leaflet-container": {
                                            position: "relative !important",
                                        },
                                    }}
                                >
                                    <MapContainer
                                        key={selectedInterestPoint?.id ?? "new"}
                                        id="leaflet-container map-container"
                                        center={getMapCenter}
                                        style={{
                                            height: 320,
                                            width: "100%",
                                            transition: "all 0.5s ease-out allow-discrete",
                                        }}
                                        boxZoom={true}
                                        zoomControl={true}
                                        attributionControl
                                        zoomAnimation={true}
                                        zoom={hasValidPosition ? 15 : 4}
                                    >
                                        <TileLayer
                                            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        <LocationPicker
                                            disabled={isReadOnly}
                                            onPick={(lat, lng) => {
                                                setInterestPointLatitude(lat.toFixed(6));
                                                setInterestPointLongitude(lng.toFixed(6));
                                                reverseGeocode(lat, lng);
                                            }}
                                        />
                                        {hasValidPosition && (
                                            <>
                                                <RecenterMap lat={Number(interestPointLatitude)} lng={Number(interestPointLongitude)} />
                                                <Marker
                                                    position={[Number(interestPointLatitude), Number(interestPointLongitude)]}
                                                    icon={pinIcon}
                                                    draggable={!isReadOnly}
                                                    eventHandlers={{
                                                        dragend: (e) => {
                                                            const marker = e.target;
                                                            const pos = marker.getLatLng();
                                                            setInterestPointLatitude(pos.lat.toFixed(6));
                                                            setInterestPointLongitude(pos.lng.toFixed(6));
                                                            reverseGeocode(pos.lat, pos.lng);
                                                        },
                                                    }}
                                                />
                                            </>
                                        )}
                                    </MapContainer>
                                </Box>
                                {hasValidPosition ? (
                                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                                        Coordenadas selecionadas: {Number(interestPointLatitude).toFixed(6)}, {Number(interestPointLongitude).toFixed(6)}
                                    </Typography>
                                ) : (
                                    !isReadOnly && (
                                        <Typography variant="caption" sx={{ color: "#dc3545", display: "block", mt: 1 }}>
                                            Nenhum ponto selecionado ainda.
                                        </Typography>
                                    )
                                )}
                            </Box>

                        </>
                    ) : (
                        loadingEsqueleton
                    )}

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={() => {
                                if (!isToastVisible) verifyBeforeCancel();
                            }}
                        >
                            <CloseOutlined style={{ marginRight: 5 }} /> Fechar
                        </Button>
                        <Button
                            type="button"
                            variant="contained"
                            disabled={!inEdit && !inInsert}
                            onClick={() => (inInsert && !inEdit ? onSave() : onUpdate())}
                            startIcon={<SaveOutlined />}
                        >
                            {loading ? <Skeleton variant="circular" width={20} height={20} /> : "Salvar"}
                        </Button>
                    </Box>
                </Paper>
            </Rmodal>
        );
    }, [
        isOpenDetails,
        loading,
        loadingEsqueleton,
        validationText,
        isToastVisible,
        selectedInterestPoint,
        isReadOnly,
        inEdit,
        inInsert,
        interestPointName,
        interestPointDescription,
        interestPointAddress,
        interestPointColor,
        interestPointLatitude,
        interestPointLongitude,
        handleChange,
        onSave,
        onUpdate,
        verifyBeforeCancel,
        verifyBeforeUpdateStatus,
        reverseGeocode,
        location?.state?.canEdit,
        getMapCenter,
        pinIcon,
        hasValidPosition,
    ]);

    return (
        <>
            {DetailsModal}
            <Toast
                ref={toastRef}
                position="center"
                onShow={() => {
                    setIsToastVisible(true);
                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <GridTemplate
                columns={columns}
                headerName="Pontos de interesse"
                isHiddenMenu={isHiddenMenu}
                isLoading={loading}
                order={"asc"}
                rows={rowsOptions(filteredList, (item: any) => {
                    setIsOpenDetails(true);
                    setSelectedInterestPoint(item);
                })}
                sortField="name"
                withFilter={true}
                withOptions={true}
                query={query}
                setQuery={(value: string) => setQuery(value)}
                filterPlaceholder={"Nome ou descrição"}
                onRefresh={getAllInterestPoints}
                onExport={async () => {}}
                disableReportButton={true}
                disableAddButton={!location?.state?.canCreate}
                extraOptions={interestPointsList.some((item) => item.activated) && (
                    <IconButton
                        onClick={() => {
                            const activePoints = interestPointsList.filter((item) => item.activated);
                            if (activePoints.length === 0) return;
                            openWhatsAppShare(buildAllInterestPointsShareMessage(activePoints));
                        }}
                        title="Compartilhar todos os pontos ativos"
                        sx={{
                            background: "#25D366",
                            width: "32px",
                            height: "32px",
                        }}
                    >
                        <WhatsApp style={{ color: "#ffff", fontSize: "1.1rem" }} />
                    </IconButton>
                )}
                handleNext={(activated: string, page: number, size: number) => getAllInterestPoints(activated, page, size)}
                handlePrevious={() => {}}
                isLastPage={isLastPage}
                totalPerPage={numberOfElementsPerPage}
                currentPage={pageNumber}
                totalElements={numberOfElements}
                onAdd={() => {
                    setIsOpenDetails(true);
                    setInInsert(true);
                    setInterestPointColor("#003f4a");
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
                // O GridTemplate tipa a linha como GeofenceType; aqui a linha é um ponto de interesse.
                onClickRow={(row) => {
                    const item = row as unknown as InterestPointType;
                    setIsOpenDetails(true);
                    setSelectedInterestPoint(item);
                    handleOnEditValues(item);
                    setLoading(true);
                    setTimeout(() => {
                        setLoading(false);
                    }, 2000);
                }}
            />
        </>
    );
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "#f3f4f6",
    border: "none",
    boxShadow: "none",
    borderRadius: 1,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 3,
    fontFamily: "Prometo",
};

export default InterestPointPage;
